import { FiBarChart2 } from 'react-icons/fi';

const advancedTask1Exercises = [
  {
    title: 'Band 8 Overview Clinic',
    type: 'writing',
    difficulty: 'Hard',
    bandLevel: 'Band 7.5-8.5',
    aiReviewFocus: ['overview quality', 'feature selection', 'data grouping', 'band 8 tone'],
    instructions:
      'Write a high-band overview that highlights only the biggest features, not every detail.',
    questions: [
      {
        text: 'A table shows household spending in 2000 and 2025. Housing stayed the largest category, transport overtook food by 2025, and leisure remained the smallest item in both years.',
        example:
          'Overall, housing accounted for the greatest share of household expenditure in both years, while leisure was consistently the smallest category. Another notable feature is that transport rose enough to surpass food by 2025.',
        evaluationMode: 'ai-review',
        aiReviewFocus: ['main-feature selection', 'comparative language', 'avoiding detail overload'],
        tip: 'Mention the two or three standout patterns only.',
      },
      {
        text: 'A process diagram shows how coffee beans are produced, from planting to export. There are eight stages, and the final stages involve grading, packaging, and shipment.',
        example:
          'Overall, coffee production is a linear eight-stage process that begins with cultivation and ends with the beans being prepared and shipped for export. The later stages focus mainly on quality control and distribution.',
        evaluationMode: 'ai-review',
        aiReviewFocus: ['process overview', 'sequencing language', 'passive style'],
        tip: 'For process diagrams, summarize the start, end, and broad stage groups.',
      },
    ],
  },
];

export const task1OverviewData = {
  courseId: 'task1-overview',
  courseType: 'writing-course',
  title: 'Task 1 Overview',
  icon: FiBarChart2,
  overview: {
    impact: 'Master data description for Band 7+',
    focus: 'Describing charts, graphs, diagrams',
    uses: 'IELTS Writing Task 1',
    duration: '30 minutes',
  },
  sections: [
    {
      title: 'Task 1 Types',
      content: `<p>IELTS Academic Writing Task 1 requires you to describe visual information:</p>`,
      table: {
        headers: ['Type', 'What It Shows', 'Key Focus'],
        rows: [
          ['Line Graph', 'Trends over time', 'Changes, peaks, troughs'],
          ['Bar Chart', 'Comparisons', 'Differences, similarities'],
          ['Pie Chart', 'Proportions/Percentages', 'Relative sizes'],
          ['Table', 'Specific figures', 'Key numbers, comparisons'],
          ['Process Diagram', 'Steps/Stages', 'Sequence, passive voice'],
          ['Map', 'Changes/Locations', 'Directions, development'],
        ],
      },
    },
    {
      title: 'Task 1 Structure',
      content: `<p>Follow this 4-paragraph structure for all Task 1 responses:</p>`,
      points: [
        '<strong>Introduction</strong> (1-2 sentences): Paraphrase the question',
        '<strong>Overview</strong> (2-3 sentences): Summarize main trends/features — THIS IS ESSENTIAL',
        '<strong>Body 1</strong>: Describe first main feature with specific data',
        '<strong>Body 2</strong>: Describe second main feature with specific data',
      ],
      warning: 'Missing an overview can seriously hurt your score! Always include one.',
    },
    {
      title: 'Key Vocabulary for Trends',
      content: `<p>Use these words to describe changes in data:</p>`,
      table: {
        headers: ['Movement', 'Verbs', 'Nouns'],
        rows: [
          ['Increase', 'rose, increased, grew, climbed', 'rise, increase, growth'],
          ['Decrease', 'fell, dropped, declined, decreased', 'fall, drop, decline'],
          ['Fluctuation', 'fluctuated, varied', 'fluctuation, variation'],
          ['Stability', 'remained stable, stayed constant', 'stability, plateau'],
          ['Peak', 'peaked, reached a peak', 'peak, high point'],
        ],
      },
    },
    {
      title: 'Degree Words',
      content: `<p>Add adverbs and adjectives to show how much change occurred:</p>`,
      points: [
        '<strong>Dramatic:</strong> dramatically, sharply, significantly, considerably',
        '<strong>Moderate:</strong> steadily, gradually, moderately',
        '<strong>Small:</strong> slightly, marginally, minimally',
      ],
      examples: [
        { label: 'Dramatic', text: 'Sales rose sharply from 100 to 500.' },
        { label: 'Moderate', text: 'Revenue increased gradually over five years.' },
        { label: 'Small', text: 'The number remained relatively stable, with only a slight increase.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Choose the Right Vocabulary',
      type: 'select',
      instructions: 'Select the best word to describe each change.',
      questions: [
        {
          text: 'Sales went from $10 million to $50 million in one year.',
          options: ['rose dramatically', 'increased slightly', 'remained stable'],
          answer: 'rose dramatically',
          explanation: 'A 5x increase is dramatic.',
        },
        {
          text: 'The figure stayed at approximately 25% for 10 years.',
          options: ['remained stable', 'fluctuated', 'declined'],
          answer: 'remained stable',
          explanation: 'Same value over time = stability.',
        },
        {
          text: 'Numbers went from 100 to 98 over the period.',
          options: ['decreased marginally', 'dropped sharply', 'fell significantly'],
          answer: 'decreased marginally',
          explanation: 'A 2% change is marginal.',
        },
        {
          text: 'The data went up and down throughout the period.',
          options: ['fluctuated', 'declined steadily', 'remained stable'],
          answer: 'fluctuated',
          explanation: 'Up and down movement = fluctuation.',
        },
        {
          text: 'The number reached its highest point in 2015.',
          options: ['peaked', 'dropped', 'stabilized'],
          answer: 'peaked',
          explanation: 'Highest point = peak.',
        },
      ],
    },
    {
      title: 'Identify Chart Types',
      type: 'select',
      instructions: 'What type of visual requires this description approach?',
      questions: [
        {
          text: 'Description of steps in a manufacturing process',
          options: ['Process Diagram', 'Line Graph', 'Pie Chart'],
          answer: 'Process Diagram',
          explanation: 'Process diagrams show sequential steps.',
        },
        {
          text: 'Changes in population over 50 years',
          options: ['Line Graph', 'Bar Chart', 'Map'],
          answer: 'Line Graph',
          explanation: 'Line graphs show trends over time.',
        },
        {
          text: 'Breakdown of budget spending by category',
          options: ['Pie Chart', 'Line Graph', 'Process Diagram'],
          answer: 'Pie Chart',
          explanation: 'Pie charts show proportions/percentages.',
        },
      ],
    },
    {
      title: 'Fill in Trend Words',
      type: 'fill-in',
      instructions: 'Type the correct verb form to describe the trend.',
      questions: [
        {
          text: 'Sales ___ dramatically from 100 to 500. (increase)',
          answer: 'increased',
          acceptableAnswers: ['rose', 'grew'],
          evaluationMode: 'multi-answer',
          explanation: 'Use past tense for describing historical data.',
        },
        {
          text: 'The number of visitors ___ at around 2000. (peak)',
          answer: 'peaked',
          acceptableAnswers: ['reached a peak'],
          evaluationMode: 'multi-answer',
          explanation: '"Peaked" describes reaching the highest point.',
        },
        {
          text: 'Exports ___ steadily throughout the decade. (decline)',
          answer: 'declined',
          acceptableAnswers: ['fell'],
          evaluationMode: 'multi-answer',
          explanation: 'Use past tense for historical trends.',
        },
      ],
    },
    {
      title: 'Write an Overview',
      type: 'writing',
      instructions: 'Write an overview sentence for each scenario.',
      questions: [
        {
          text: 'A line graph shows car sales rising from 1990 to 2010, then declining until 2020.',
          example: 'Overall, car sales experienced a general upward trend in the first two decades before declining in the subsequent period.',
          evaluationMode: 'ai-review',
          aiReviewFocus: ['overview sentence', 'trend summary', 'avoiding figures'],
          tip: 'Identify the main trend(s) without specific numbers.',
        },
        {
          text: 'A pie chart shows 45% prefer coffee, 30% prefer tea, 25% prefer other drinks.',
          example: 'Overall, coffee was the most popular beverage, followed by tea, while other drinks accounted for the smallest proportion.',
          evaluationMode: 'ai-review',
          aiReviewFocus: ['ranking', 'main-feature selection', 'overview language'],
          tip: 'Focus on the most notable features.',
        },
        {
          text: 'A bar chart compares male and female university enrollment, with females higher in arts and males higher in engineering.',
          example: 'Overall, there were clear gender differences in subject choice, with females dominating arts subjects while males outnumbered females in engineering.',
          evaluationMode: 'ai-review',
          aiReviewFocus: ['comparison', 'overview quality', 'grouping data'],
          tip: 'Highlight the main contrast or comparison.',
        },
      ],
    },
    {
      title: 'Complete Data Sentences',
      type: 'writing',
      instructions: 'Write sentences including specific data.',
      questions: [
        {
          text: 'Data: Car sales 2010: 2 million → 2020: 3.5 million',
          example: 'Car sales rose significantly from 2 million in 2010 to 3.5 million in 2020, representing a 75% increase.',
          evaluationMode: 'ai-review',
          aiReviewFocus: ['data accuracy', 'trend language', 'sentence control'],
          tip: 'Include both figures and show the change.',
        },
        {
          text: 'Data: UK exports: 40%, USA: 30%, China: 20%, Others: 10%',
          example: 'The UK accounted for the largest proportion of exports at 40%, followed by the USA with 30%, while China and other countries made up 20% and 10% respectively.',
          evaluationMode: 'ai-review',
          aiReviewFocus: ['ordering', 'data coverage', 'comparative range'],
          tip: 'Order from largest to smallest, use linking words.',
        },
      ],
    },
  ],
  tips: [
    'Always include an overview—it\'s worth up to 25% of your mark',
    'Use a range of vocabulary, not just "increased" and "decreased"',
    'Include specific data (numbers, percentages) in body paragraphs',
    'Write at least 150 words but don\'t exceed 200',
  ],
  dailyPractice: [
    'Practice writing overviews for different chart types',
    'Build your trend vocabulary with flashcards',
    'Time yourself: complete Task 1 in 20 minutes',
    'Analyze model answers for structure and vocabulary',
  ],
  backPath: '/exams/english/#writing',
  backLabel: 'Back to Writing',
  aiContext: 'IELTS Writing Task 1 Overview: Describing charts, graphs, and diagrams using appropriate structure, trend vocabulary, and degree words.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...task1OverviewData,
      title: task1OverviewData.title + ' (Foundations)',
      overview: {
        ...task1OverviewData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: task1OverviewData.sections.slice(0, Math.max(2, Math.ceil(task1OverviewData.sections.length / 2))),
      exercises: task1OverviewData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...task1OverviewData,
      title: task1OverviewData.title + ' (Advanced)',
      overview: {
        ...task1OverviewData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: task1OverviewData.sections,
      exercises: [
        ...task1OverviewData.exercises.map(ex => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
        ...advancedTask1Exercises,
      ],
      tips: [
        ...(task1OverviewData.tips || []),
        'For Band 8+, group related features instead of listing every number in order.',
        'Advanced tip: keep the overview data-light and reserve exact figures for the body paragraphs.',
      ],
    };
  }
  return task1OverviewData;
}
